import { useState, useEffect } from "react";
import { retrieveLaunchParams, shareURL } from "@telegram-apps/sdk";
import { Button } from "@telegram-apps/telegram-ui";
import ApiService from "../../handlers/apiService";
import { useI18n } from "../../utils/i18n/I18nProvider";
import { InviteStats } from "../../handlers/types";
import "./Invite.css";
import { TELEGRAM_APP_URL } from "../../configs/config";
import React from "react";
import CheckIcon from "../Tasks/CheckIcon";

export function Invite() {
  const { t } = useI18n();
  const [inviteLink, setInviteLink] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const [inviteStats, setInviteStats] = useState<InviteStats>({
    usersInvited: 0,
    nextBonus: 0,
    earned: 0,
  });

  useEffect(() => {
    // Get current user ID from launch params
    try {
      const params = retrieveLaunchParams();
      if (params.tgWebAppData?.user?.id) {
        setInviteLink(
          `${TELEGRAM_APP_URL}?startapp=${params.tgWebAppData.user.id}`
        );
      }
    } catch (error) {
      console.error("Failed to get user ID:", error);
    }

    // Fetch invite stats
    fetchInviteStats();
  }, []);

  const fetchInviteStats = async () => {
    try {
      setIsLoading(true);
      const api = ApiService.getInstance();
      const response = await api.api.usersInviteStatsList();

      if (response) {
        setInviteStats(response);
      }
    } catch (error) {
      console.error("Failed to fetch invite stats:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setIsCopied(true);

      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  const shareInviteLink = () => {
    try {
      shareURL(inviteLink, t("toolkit.invite.shareMessage"));
    } catch (error) {
      console.error("Failed to share:", error);
    }
  };

  // Check if milestone is completed based on current invite count
  const isMilestoneCompleted = (milestone: number) => {
    return inviteStats.usersInvited >= milestone;
  };

  const copyIcon = (
    <span className="invite-button-icon">
      {isCopied ? (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z"
            fill="currentColor"
          />
        </svg>
      ) : (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M16 8V5.2C16 4.0799 16 3.51984 15.782 3.09202C15.5903 2.71569 15.2843 2.40973 14.908 2.21799C14.4802 2 13.9201 2 12.8 2H5.2C4.0799 2 3.51984 2 3.09202 2.21799C2.71569 2.40973 2.40973 2.71569 2.21799 3.09202C2 3.51984 2 4.0799 2 5.2V12.8C2 13.9201 2 14.4802 2.21799 14.908C2.40973 15.2843 2.71569 15.5903 3.09202 15.782C3.51984 16 4.0799 16 5.2 16H8M11.2 22H18.8C19.9201 22 20.4802 22 20.908 21.782C21.2843 21.5903 21.5903 21.2843 21.782 20.908C22 20.4802 22 19.9201 22 18.8V11.2C22 10.0799 22 9.51984 21.782 9.09202C21.5903 8.71569 21.2843 8.40973 20.908 8.21799C20.4802 8 19.9201 8 18.8 8H11.2C10.0799 8 9.51984 8 9.09202 8.21799C8.71569 8.40973 8.40973 8.71569 8.21799 9.09202C8 9.51984 8 10.0799 8 11.2V18.8C8 19.9201 8 20.4802 8.21799 20.908C8.40973 21.2843 8.71569 21.5903 9.09202 21.782C9.51984 22 10.0799 22 11.2 22Z"
            stroke="#171F2A"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </span>
  );

  return (
    <div className="invite-page">
      <div className="invite-content">
        {/* Header */}
        <div className="invite-header">
          <h1 className="invite-title">{t("toolkit.invite.title")}</h1>
          <p className="invite-subtitle">
            {t("toolkit.invite.subtitle")
              .split("{coin}")
              .map((part, index, array) => (
                <React.Fragment key={index}>
                  {part}
                  {index < array.length - 1 && (
                    <img
                      src="/assets/img/coin.webp"
                      alt="Coin"
                      className="invite-subtitle-coin-icon"
                    />
                  )}
                </React.Fragment>
              ))}
          </p>
        </div>

        {/* Special Rewards Section */}
        <div className="invite-special-rewards-section">
          <div className="invite-rewards-container">
            <h2 className="invite-rewards-title">
              {t("toolkit.invite.specialRewards")}
            </h2>

            <div className="invite-reward-item">
              <div className="invite-reward-icon">
                <img
                  src="/assets/img/coin.webp"
                  alt="Coin"
                  className="invite-reward-item-icon"
                />
              </div>
              <div className="invite-reward-content">
                <div className="invite-reward-text">
                  {t("toolkit.invite.invite1Fren")}
                </div>
                <div className="invite-reward-value">
                  {t("toolkit.invite.reward1Fren")}
                </div>
              </div>
              {isMilestoneCompleted(1) && (
                <div className="invite-reward-checkmark">
                  <CheckIcon />
                </div>
              )}
            </div>

            <div className="invite-reward-item">
              <div className="invite-reward-icon">
                <img
                  src="/assets/img/ui/explosion_and_heal.webp"
                  alt="Explosion"
                  className="invite-reward-item-icon"
                  style={{
                    width: "50px",
                    height: "45px",
                  }}
                />
              </div>
              <div className="invite-reward-content">
                <div className="invite-reward-text">
                  {t("toolkit.invite.invite5Frens")}
                </div>
                <div className="invite-reward-value">
                  {t("toolkit.invite.reward5Frens")}
                </div>
              </div>
              {isMilestoneCompleted(5) && (
                <div className="invite-reward-checkmark">
                  <CheckIcon />
                </div>
              )}
            </div>

            <div className="invite-reward-item">
              <div className="invite-reward-icon invite-character-icon">
                <div
                  className="invite-character-icon-frame"
                  style={{
                    background: `url(/assets/img/characters/char9.webp) -48px 172px`,
                  }}
                  aria-label={t("looks.character", {
                    defaultValue: "Character: {{name}}",
                    name: "Legendary",
                  })}
                  role="img"
                />
              </div>
              <div className="invite-reward-content">
                <div className="invite-reward-text">
                  {t("toolkit.invite.invite10Frens")}
                </div>
                <div className="invite-reward-value">
                  {t("toolkit.invite.reward10Frens")}
                </div>
              </div>
              {isMilestoneCompleted(10) && (
                <div className="invite-reward-checkmark">
                  <CheckIcon />
                </div>
              )}
            </div>

            <div className="invite-reward-item">
              <div className="invite-reward-icon">
                <img
                  src="/assets/img/ui/energy_full.webp"
                  alt="Energy"
                  className="invite-reward-item-icon"
                />
              </div>
              <div className="invite-reward-content">
                <div className="invite-reward-text">
                  {t("toolkit.invite.invite20Frens")}
                </div>
                <div className="invite-reward-value">
                  {t("toolkit.invite.reward20Frens")}
                </div>
              </div>
              {isMilestoneCompleted(20) && (
                <div className="invite-reward-checkmark">
                  <CheckIcon />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bonus Section */}
        <div className="invite-bonus-section">
          <h2 className="invite-section-title">
            {t("toolkit.invite.bonusForInviting")}
          </h2>

          <div className="invite-bonus-container">
            <p className="invite-bonus-description">
              {t("toolkit.invite.bonusDescription")}
            </p>

            <div className="invite-bonus-tiers">
              <div className="invite-bonus-tier">
                <span className="invite-tier-range">
                  {t("toolkit.invite.tier1Fren")}
                </span>
                <div className="invite-tier-reward">
                  <span className="invite-tier-amount">5000</span>
                  <img
                    src="/assets/img/coin.webp"
                    alt="Coin"
                    className="invite-tier-coin"
                  />
                </div>
              </div>

              <div className="invite-bonus-tier">
                <span className="invite-tier-range">
                  {t("toolkit.invite.tier2to10Frens")}
                </span>
                <div className="invite-tier-reward">
                  <span className="invite-tier-amount">300</span>
                  <img
                    src="/assets/img/coin.webp"
                    alt="Coin"
                    className="invite-tier-coin"
                  />
                </div>
              </div>

              <div className="invite-bonus-tier">
                <span className="invite-tier-range">
                  {t("toolkit.invite.tier10to50Frens")}
                </span>
                <div className="invite-tier-reward">
                  <span className="invite-tier-amount">150</span>
                  <img
                    src="/assets/img/coin.webp"
                    alt="Coin"
                    className="invite-tier-coin"
                  />
                </div>
              </div>

              <div className="invite-bonus-tier">
                <span className="invite-tier-range">
                  {t("toolkit.invite.tier50to500Frens")}
                </span>
                <div className="invite-tier-reward">
                  <span className="invite-tier-amount">10</span>
                  <img
                    src="/assets/img/coin.webp"
                    alt="Coin"
                    className="invite-tier-coin"
                  />
                </div>
              </div>

              <div className="invite-bonus-tier">
                <span className="invite-tier-range">
                  {t("toolkit.invite.tier500PlusFrens")}
                </span>
                <div className="invite-tier-reward">
                  <span className="invite-tier-amount">
                    {t("toolkit.invite.customDeal")}
                  </span>
                </div>
              </div>
            </div>

            <p className="invite-bonus-note">{t("toolkit.invite.bonusNote")}</p>
          </div>
        </div>

        {/* Delimiter */}
        <div className="invite-section-delimiter"></div>

        {/* Your Frens Section */}
        <div className="invite-your-frens-section">
          <h2 className="invite-your-frens-section-title">
            {t("toolkit.invite.yourFrens")}
          </h2>

          <div className="invite-frens-container">
            {!isLoading && (
              <div className="invite-frens-stats">
                <div className="invite-frens-stat">
                  <span className="invite-stat-label">
                    {t("toolkit.invite.yourFrensLabel")}
                  </span>
                  <span className="invite-stat-value">
                    {inviteStats.usersInvited || 0}
                  </span>
                </div>
                <div className="invite-frens-stat">
                  <span className="invite-stat-label">
                    {t("toolkit.invite.youEarned")}
                  </span>
                  <div className="invite-stat-reward">
                    <span className="invite-stat-amount">
                      {inviteStats.earned}
                    </span>
                    <img
                      src="/assets/img/coin.webp"
                      alt="Coin"
                      className="invite-stat-coin"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sticky Buttons */}
      <div className="invite-buttons-sticky">
        <Button
          className="invite-copy-button"
          mode="bezeled"
          size="l"
          stretched
          after={copyIcon}
          onClick={copyToClipboard}
        >
          {t("toolkit.invite.copyReferralLink")}
        </Button>

        <Button
          className="invite-share-button"
          mode="filled"
          size="l"
          stretched
          onClick={shareInviteLink}
        >
          {t("common.share")}
        </Button>
      </div>
    </div>
  );
}

export default Invite;
